import React from "react";
import Box from "@mui/material/Box";
import {
	Typography,
	QuizContainer,
	ClassFileUploader,
	VideoChapters,
	FavoriteButton,
} from "../../../";
import {
	ViewClassHeader,
	MaterialsList,
	CourseInfo,
	NextPrevNavigation,
} from "../";
import { ClassRatings } from "../../ClassRatings";
import { VideoPlayer } from "../VideoPlayer";
import { QuizPreviousAnswers } from "../QuizPreviousAnswers";
import { PromotionalBanner } from "../PromotionalBanner";
import { DebateProvider } from "../../../../context/debate/debateContext";
import CourseDetailDebates from "../../../../pages/CourseDetail/CourseDetailDebates";
import {
	ClassContentWrapper,
	LeiaMaisButtonWrapper,
	ContentSection,
	QuizSection,
	DescriptionWrapper,
} from "./LessonContentStyled";
import { ModuleClass, Module, Course, Material, QuizAnswerResponse } from "../../../../types/course";
import { IPandaEvent } from "../../../../hooks/useVideoPlayer";

interface LessonContentProps {
	// Lesson data
	activeModuleClass: ModuleClass;
	activeModule: Module;
	course: Course;

	// Video player props
	videoUrl?: string;
	playing: boolean;
	playerRef: React.RefObject<any>;
	startTime: number;
	onStartVideo: (isPanda: boolean, pandaEvent?: IPandaEvent) => Promise<void>;
	onPauseVideo: (isPanda: boolean, pandaEvent?: IPandaEvent) => Promise<void>;
	onProgressVideo: (isFinished: boolean, isPanda: boolean, pandaEvent?: IPandaEvent, progressData?: any) => Promise<void>;
	pauseVideo: () => void;
	seekTo: (time: number) => void;

	// Rating props
	lessonRating: number;
	lessonRatingKey: number;
	onRate: (lid: number, rating: number) => void;

	// Description truncation props
	truncateDescription: boolean;
	showTruncateButton: boolean;
	onToggleTruncate: () => void;

	// Quiz props
	previousAnswers: QuizAnswerResponse[];
	onQuizAnswer: (answer: QuizAnswerResponse) => void;
}

/**
 * Main content area for a lesson
 * Orchestrates video player, lesson info, ratings, quiz, and debates
 */
const LessonContent: React.FC<LessonContentProps> = ({
	activeModuleClass,
	activeModule,
	course,
	videoUrl,
	playing,
	playerRef,
	startTime,
	onStartVideo,
	onPauseVideo,
	onProgressVideo,
	pauseVideo,
	seekTo,
	lessonRating,
	lessonRatingKey,
	onRate,
	truncateDescription,
	showTruncateButton,
	onToggleTruncate,
	previousAnswers,
	onQuizAnswer,
}) => {
	return (
		<>
			<ViewClassHeader
				activeLesson={activeModuleClass}
				name={activeModuleClass.name}
				moduleName={activeModule.nome}
				releaseDate={activeModuleClass.releaseDate}
			/>

			<ClassContentWrapper>
				{/* Video Player */}
				{videoUrl && (
					<VideoPlayer
						videoUrl={videoUrl}
						playing={playing}
						playerRef={playerRef}
						startTime={startTime}
						onPlay={onStartVideo}
						onPause={onPauseVideo}
						onProgress={onProgressVideo}
						onEnded={onProgressVideo}
						seekTo={seekTo}
					/>
				)}

				{/* Navigation and Ratings (non-quiz lessons only) */}
				{!activeModuleClass.isQuiz && <NextPrevNavigation />}
				{!activeModuleClass.isQuiz && (
					<ClassRatings
						lessonID={activeModuleClass.id}
						currentLessonRating={lessonRating}
						key={lessonRatingKey}
						onRate={onRate}
					/>
				)}

				{/* Main Content Section */}
				<ContentSection className="with-padding">
					<Box
						display="flex"
						width="100%"
						flexDirection={{ xs: "column", sm: "column", md: "row" }}
						justifyContent="space-between"
					>
						<DescriptionWrapper>
							{/* Quiz Info */}
							{activeModuleClass.isQuiz && activeModule.quizObject && (
								<>
									<QuizPreviousAnswers
										previousAnswers={previousAnswers}
										chances={activeModule.quizObject.chances}
									/>
									{activeModuleClass.description && (
										<p style={{ marginBottom: "-10px" }}>
											<strong>Descrição do Questionário</strong>
										</p>
									)}
								</>
							)}

							{/* Video Chapters */}
							{videoUrl && activeModuleClass.timeline && activeModuleClass.timeline.length > 0 && (
								<VideoChapters
									timeline={activeModuleClass.timeline}
									seekFunction={seekTo}
								/>
							)}

							{/* Lesson Info */}
							<CourseInfo
								name={activeModuleClass.isQuiz ? "" : activeModuleClass.name}
								favBtn={
									<FavoriteButton
										refID={activeModuleClass.id}
										buttonStyle="simple"
										favType="AULA"
									/>
								}
								description={activeModuleClass.description}
								truncateDescription={truncateDescription}
							/>

							{/* File Upload */}
							{activeModuleClass.acceptsUpload && (
								<ClassFileUploader
									maxFiles={activeModuleClass.maxUploadableFiles}
									classId={activeModuleClass.id}
								/>
							)}

							{/* Read More Button */}
							{showTruncateButton && (
								<LeiaMaisButtonWrapper>
									<button
										onClick={onToggleTruncate}
										aria-expanded={!truncateDescription}
									>
										{truncateDescription ? "Leia Mais" : "Recolher"}
									</button>
								</LeiaMaisButtonWrapper>
							)}
						</DescriptionWrapper>

						{/* Materials List */}
						{activeModuleClass.materials.length > 0 && (
							<MaterialsList>
								<Typography
									variant="title"
									size="18px"
									margin="0 0 21px 0"
									fontWeight="bold"
								>
									Materiais para Download
								</Typography>
								{activeModuleClass.materials.map((material: Material) => (
									<li key={material.id}>
										<a
											href={material.url}
											target="_blank"
											rel="noopener noreferrer"
											title={material.name}
										>
											{material.name}
										</a>
									</li>
								))}
							</MaterialsList>
						)}
					</Box>
				</ContentSection>

				{/* Promotional Banner */}
				<PromotionalBanner lessonId={activeModuleClass.id} />

				{/* Quiz Section */}
				{activeModuleClass.isQuiz && activeModule.quizObject && (
					<QuizSection>
						<QuizContainer
							onAnswer={onQuizAnswer}
							moduleId={activeModule.id}
							quizObject={activeModule.quizObject}
						/>
					</QuizSection>
				)}
			</ClassContentWrapper>

			{/* Debates Section (non-quiz lessons with comments enabled) */}
			{activeModuleClass.showComments && !activeModuleClass.isQuiz && (
				<DebateProvider>
					<CourseDetailDebates
						timestamp={activeModuleClass.timestamp}
						course={course}
						activeModuleClass={activeModuleClass}
						onAction={pauseVideo}
					/>
				</DebateProvider>
			)}
		</>
	);
};

export { LessonContent };
